import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Search, UserPlus } from "lucide-react";
import { staffApi, staffQueryKeys, type StaffListFilter } from "../../shared/api/staff";
import { useDebounce } from "../../shared/hooks/useDebounce";
import { param, useUrlFilters } from "../../shared/hooks/useUrlFilters";
import type {
  InviteStatus,
  StaffRole,
  StaffStatus,
  StaffSummaryResponse,
} from "../../shared/types/staff";
import {
  formatDate,
  formatPhone,
  inviteStatusLabel,
  staffRoleLabel,
  staffStatusLabel,
} from "../../shared/format";
import { apiError } from "../../shared/api/client";
import {
  BulkDeleteBar,
  runBulkDelete,
  useRowSelection,
} from "../../shared/ui/bulkSelect";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const PAGE_SIZE = 20;
type Tab = "all" | "invites";

type BadgeVariant = NonNullable<BadgeProps["variant"]>;

const STAFF_STATUS_VARIANT: Record<string, BadgeVariant> = {
  ACTIVE: "success",
  INACTIVE: "muted",
  SUSPENDED: "warning",
  ARCHIVED: "muted",
};

const nativeSelectClass =
  "h-9 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

const checkboxClass =
  "h-4 w-4 shrink-0 rounded-sm border-primary text-primary accent-primary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2";

export function UsersSettingsPage() {
  const qc = useQueryClient();
  // Tab + bộ lọc nằm trên URL: reload hay quay về từ một hồ sơ vẫn đúng chỗ. Vắng mặt = tab
  // "Tất cả nhân viên", không lọc.
  const { filters, set } = useUrlFilters({
    tab: param.oneOf(["all", "invites"] as const, "all"),
    q: param.string(),
    role: param.oneOf(Object.keys(staffRoleLabel) as StaffRole[]),
    status: param.oneOf(Object.keys(staffStatusLabel) as StaffStatus[]),
    page: param.int(),
  });
  const { tab, q, role: staffRole, status, page } = filters;
  const debouncedQ = useDebounce(q.trim(), 300);
  const setTab = (t: Tab) => set({ tab: t });

  const sel = useRowSelection();
  const [bulkPending, setBulkPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filter: StaffListFilter = {
    q: debouncedQ || null,
    staffRole: staffRole || null,
    status: status || null,
  };

  const staffList = useQuery({
    queryKey: staffQueryKeys.list(filter, page),
    queryFn: () =>
      staffApi.list(filter, { page, size: PAGE_SIZE, sort: "updatedAt,desc" }),
    placeholderData: (prev) => prev,
    enabled: tab === "all",
  });

  const rows = staffList.data?.content ?? [];
  const allSelected = rows.length > 0 && rows.every((s) => sel.selectedIds.has(s.id));
  const someSelected = rows.some((s) => sel.selectedIds.has(s.id)) && !allSelected;
  function toggleAll() {
    const next = new Set(sel.selectedIds);
    if (allSelected) rows.forEach((s) => next.delete(s.id));
    else rows.forEach((s) => next.add(s.id));
    sel.setSelectedIds(next);
  }

  async function onDeleteRow(id: string, name: string) {
    if (!confirm(`Xoá vĩnh viễn nhân viên "${name}"? Không thể hoàn tác.`)) return;
    try {
      await staffApi.remove(id);
      qc.invalidateQueries({ queryKey: staffQueryKeys.all });
    } catch (e) {
      setError(apiError(e).detail);
    }
  }

  return (
    <div>
      <PageHeader
        title="Người dùng & Nhân viên"
        description="Quản lý nhân viên có thể truy cập hệ thống — bác sĩ, điều dưỡng, lễ tân và quản lý phòng khám."
        actions={
          <Button asChild>
            <Link to="/settings/users/new">
              <UserPlus />
              Mời nhân viên
            </Link>
          </Button>
        }
      />

      <div className="mb-4 flex gap-1 border-b">
        <button
          type="button"
          className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            tab === "all"
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setTab("all")}
        >
          Tất cả nhân viên
        </button>
        <button
          type="button"
          className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            tab === "invites"
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setTab("invites")}
        >
          Lời mời đang chờ
        </button>
      </div>

      {tab === "all" ? (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="relative min-w-0 flex-1 sm:max-w-xs">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                type="search"
                placeholder="Tìm theo tên, email, số ĐT…"
                value={q}
                onChange={(e) => set({ q: e.target.value })}
                autoFocus
                className="pl-9"
              />
            </div>
            <select
              value={staffRole}
              onChange={(e) => set({ role: e.target.value as StaffRole | "" })}
              aria-label="Lọc theo vai trò"
              className={nativeSelectClass}
            >
              <option value="">Mọi vai trò</option>
              {(Object.keys(staffRoleLabel) as StaffRole[]).map((r) => (
                <option key={r} value={r}>
                  {staffRoleLabel[r]}
                </option>
              ))}
            </select>
            <select
              value={status}
              onChange={(e) => set({ status: e.target.value as StaffStatus | "" })}
              aria-label="Lọc theo trạng thái"
              className={nativeSelectClass}
            >
              <option value="">Mọi trạng thái</option>
              {(Object.keys(staffStatusLabel) as StaffStatus[]).map((s) => (
                <option key={s} value={s}>
                  {staffStatusLabel[s]}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <BulkDeleteBar
            count={sel.count}
            noun="nhân viên"
            pending={bulkPending}
            onClear={sel.clear}
            onDelete={async () => {
              setBulkPending(true);
              setError(null);
              const { failed } = await runBulkDelete(
                [...sel.selectedIds],
                staffApi.remove
              );
              sel.clear();
              qc.invalidateQueries({ queryKey: staffQueryKeys.all });
              if (failed.length) setError(`${failed.length} mục không xoá được.`);
              setBulkPending(false);
            }}
          />

          <div className="rounded-md border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-9 text-center">
                    <input
                      type="checkbox"
                      aria-label="Chọn tất cả"
                      className={checkboxClass}
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected;
                      }}
                      onChange={toggleAll}
                    />
                  </TableHead>
                  <TableHead>Họ &amp; Tên</TableHead>
                  <TableHead>Vai trò</TableHead>
                  <TableHead>Trạng thái</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Số ĐT</TableHead>
                  <TableHead>Cập nhật</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {staffList.isLoading && (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                      Đang tải…
                    </TableCell>
                  </TableRow>
                )}
                {staffList.isError && (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center text-destructive">
                      ⚠ {apiError(staffList.error).detail}
                    </TableCell>
                  </TableRow>
                )}
                {!staffList.isLoading && (staffList.data?.content.length ?? 0) === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                      Chưa có nhân viên nào. Bấm "Mời nhân viên" để bắt đầu.
                    </TableCell>
                  </TableRow>
                )}
                {staffList.data?.content.map((s) => (
                  <StaffRow
                    key={s.id}
                    s={s}
                    selected={sel.selectedIds.has(s.id)}
                    onToggle={() => sel.toggle(s.id)}
                    onDelete={() => onDeleteRow(s.id, s.fullName)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      ) : (
        <InvitesTab />
      )}
    </div>
  );
}

function StaffRow({
  s,
  selected,
  onToggle,
  onDelete,
}: {
  s: StaffSummaryResponse;
  selected: boolean;
  onToggle: () => void;
  onDelete: () => void;
}) {
  return (
    <TableRow
      className="cursor-pointer"
      onClick={() => window.location.assign(`/settings/users/${s.id}`)}
    >
      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          aria-label="Chọn dòng"
          className={checkboxClass}
          checked={selected}
          onChange={onToggle}
        />
      </TableCell>
      <TableCell>
        <span className="font-semibold text-foreground">{s.fullName}</span>
        {!s.userId && (
          <Badge variant="muted" className="ml-2">
            Chưa kích hoạt
          </Badge>
        )}
      </TableCell>
      <TableCell>{staffRoleLabel[s.staffRole]}</TableCell>
      <TableCell>
        <Badge variant={STAFF_STATUS_VARIANT[s.status] ?? "outline"}>
          {staffStatusLabel[s.status]}
        </Badge>
      </TableCell>
      <TableCell>{s.email ?? "—"}</TableCell>
      <TableCell>{formatPhone(s.phone)}</TableCell>
      <TableCell className="font-mono tabular-nums">{formatDate(s.updatedAt)}</TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <Button asChild variant="link" size="sm" className="h-auto p-0">
            <Link to={`/settings/users/${s.id}`}>Mở</Link>
          </Button>
          <Button type="button" variant="destructive" size="sm" onClick={onDelete}>
            Xoá
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function InvitesTab() {
  const qc = useQueryClient();
  // Cùng URL với tab nhân viên, khoá riêng để không đụng `status` của tab kia.
  const invitesFilter = useUrlFilters({
    inviteStatus: param.oneOf(Object.keys(inviteStatusLabel) as InviteStatus[], "SENT"),
  });
  const statusFilter = invitesFilter.filters.inviteStatus;

  const invites = useQuery({
    queryKey: staffQueryKeys.invites(statusFilter),
    queryFn: () => staffApi.listInvites(statusFilter),
  });

  const resend = useMutation({
    mutationFn: (id: string) => staffApi.resendInvite(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: staffQueryKeys.invites(statusFilter) }),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => staffApi.revokeInvite(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: staffQueryKeys.invites(statusFilter) }),
  });

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => invitesFilter.set({ inviteStatus: e.target.value as InviteStatus })}
          aria-label="Trạng thái lời mời"
          className={nativeSelectClass}
        >
          {(Object.keys(inviteStatusLabel) as InviteStatus[]).map((s) => (
            <option key={s} value={s}>
              {inviteStatusLabel[s]}
            </option>
          ))}
        </select>
      </div>
      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Vai trò</TableHead>
              <TableHead>Gửi lúc</TableHead>
              <TableHead>Hết hạn</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invites.isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  Đang tải…
                </TableCell>
              </TableRow>
            )}
            {!invites.isLoading && (invites.data?.length ?? 0) === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  Không có lời mời nào.
                </TableCell>
              </TableRow>
            )}
            {invites.data?.map((inv) => (
              <TableRow key={inv.id}>
                <TableCell>
                  <span className="font-semibold text-foreground">{inv.email}</span>
                </TableCell>
                <TableCell>{inv.roleCodes.join(", ")}</TableCell>
                <TableCell className="font-mono tabular-nums">{formatDate(inv.sentAt)}</TableCell>
                <TableCell className="font-mono tabular-nums">{formatDate(inv.expiresAt)}</TableCell>
                <TableCell>
                  {inv.status === "SENT" && (
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => resend.mutate(inv.id)}
                        disabled={resend.isPending}
                      >
                        Gửi lại link
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          if (confirm(`Hủy lời mời gửi đến ${inv.email}?`)) revoke.mutate(inv.id);
                        }}
                        disabled={revoke.isPending}
                      >
                        Hủy
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
